/**
 * Fetch the structural diff between two IFC documents (WHA-3695). Powers the
 * standalone Differences Overview modal — a pure data-only diff with NO glTF
 * load and NO IfcModelStore compare-mode state.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchIfcModelDiff, type IfcModelDiff } from "@/stores/ifcModel/repository";
import { track } from "@/lib/posthog";
import { changeCountBucket } from "@/lib/analytics/eventProps";

export function useIfcDiff(baseDocId: number | null, targetDocId: number | null) {
  const query = useQuery<IfcModelDiff | null>({
    queryKey: ["ifc-diff", baseDocId, targetDocId],
    queryFn: async () => {
      if (baseDocId == null || targetDocId == null) return null;
      return fetchIfcModelDiff(baseDocId, targetDocId);
    },
    enabled: baseDocId != null && targetDocId != null,
    // The pair is immutable, so a mounted consumer never refetches. Eviction is
    // left at the default — the per-element payload is large and every compared
    // pair would otherwise sit in the cache for the lifetime of the tab.
    staleTime: Infinity,
  });

  const diff = query.data;

  /**
   * Reported from the resolved result, never from inside `queryFn` — the
   * taxonomy's rule, because a retry runs the fetcher again and would count the
   * one comparison twice.
   *
   * A null diff means the pipeline has no diff row for this pair. Reporting
   * that as a zero-change compare would make "nothing changed" and "nothing
   * computed" the same number in the funnel, so it emits nothing at all.
   *
   * The identity is the RESOLVED RESULT, not the document pair: one comparison
   * is one event however often the consumer re-renders on it, while asking for
   * the same pair again later — the caller clears the ids when it closes, which
   * clears the ref — is a comparison the user ran a second time and counts
   * again. Keying the pair for the whole session would instead answer "distinct
   * pairs opened", which is not what `drawing_compared` means.
   */
  const reported = useRef<IfcModelDiff | null>(null);
  useEffect(() => {
    if (!diff) {
      reported.current = null;
      return;
    }
    if (reported.current === diff) return;
    reported.current = diff;
    const { added, removed, modified } = diff.summary;
    track("drawing_compared", {
      viewer: "ifc_3d",
      change_count_bucket: changeCountBucket(added + removed + modified),
    });
  }, [diff]);

  return query;
}
